import { useContext, useMemo } from 'react';
import { CartContext } from '../context/CartContext';
import { useHomePageData } from '../hooks/useHomePageData';
import { categoryPath } from '../config';

import { Hero, HeroSkeleton } from '../components/home/Hero';
import { TrustTicker } from '../components/home/TrustTicker';
import { ProductGrid } from '../components/home/ProductGrid';
import { HomeSectionRenderer } from '../components/home/HomeSectionRenderer';
import { StoresCarousel } from '../components/home/StoresCarousel';
import { Omnichannel } from '../components/home/Omnichannel';
import CompatibleAccessories from '../components/CompatibleAccessories';

/** Legacy home-section types that feed the original Trending / Best selling block */
const LEGACY_TRENDING_TYPES = ['category', 'bestseller', 'new_arrivals'];

export default function Home() {
    const { addToCart } = useContext(CartContext);
    const { sections: dynamicSections, banners, accessories, accessoryTypes, loading: sectionsLoading } =
        useHomePageData();

    const legacyProductSections = useMemo(
        () => dynamicSections.filter((s) => LEGACY_TRENDING_TYPES.includes(s.sectionType)),
        [dynamicSections]
    );

    const cmsSections = useMemo(
        () => dynamicSections.filter((s) => !LEGACY_TRENDING_TYPES.includes(s.sectionType)),
        [dynamicSections]
    );

    const trendingSection = useMemo(() => {
        const allProducts = legacyProductSections.flatMap((s) => s.products || []);
        const uniqueProductsMap = new Map();
        allProducts.forEach((p) => {
            if (!uniqueProductsMap.has(p._id)) uniqueProductsMap.set(p._id, p);
        });
        const uniqueAllProducts = Array.from(uniqueProductsMap.values());

        const combinedTabs = [
            ...(uniqueAllProducts.length > 0
                ? [{ label: 'All', products: uniqueAllProducts.slice(0, 12) }]
                : []),
            ...legacyProductSections.map((s) => ({
                label: s.title,
                products: s.products || [],
            })),
        ];

        return {
            title: 'Shop the Hottest Drops',
            subtitle: 'Trending Now',
            tabs: combinedTabs,
        };
    }, [legacyProductSections]);

    return (
        <main className="flex-1">
            {banners.length > 0 ? (
                <Hero banners={banners} />
            ) : sectionsLoading ? (
                <HeroSkeleton />
            ) : null}
            <TrustTicker />

            {sectionsLoading && (
                <div className="py-12 container mx-auto px-4">
                    <div className="animate-pulse">
                        <div className="h-8 bg-surface-elevated rounded w-48 mb-10 mx-auto sm:mx-0" />
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="h-72 bg-surface rounded-2xl" />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {!sectionsLoading && trendingSection.tabs.length > 0 && (
                <ProductGrid section={trendingSection} mode="trending" />
            )}

            {accessories.length > 0 && (
                <section className="pt-8 sm:pt-10 pb-2 container mx-auto px-4 lg:px-8 overflow-x-hidden">
                    <CompatibleAccessories
                        items={accessories.filter((a) => a.inStock)}
                        types={accessoryTypes}
                        fetchOnTabChange
                        onAddToCart={addToCart}
                        title="Trending accessories"
                        viewAllLink={categoryPath('accessories')}
                    />
                </section>
            )}

            <StoresCarousel />

            {!sectionsLoading &&
                cmsSections.map((section) => (
                    <HomeSectionRenderer key={section._id} section={section} />
                ))}

            <Omnichannel />
        </main>
    );
}
